import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { TaskbookService } from "../../taskbook-triage/taskbook.service";
import {
    AddAssigneeTaskActionDTO,
    AddThreadTaskActionDTO,
    ArchiveTaskActionDTO,
    CreateKudosTaskActionDTO,
    CreatePollTaskActionDTO,
    ManagerCommentTaskActionDTO, RemoveAssigneeTaskActionDTO,
    Review,
    Task,
    TaskAction,
    TaskActionDTO,
    TaskAssignee,
    TaskLabel,
    UpdateLabelTaskActionDTO
} from "../../taskbook-triage/taskbook.types";
import { UserInfos } from "../../shared/ui/username-icon/user-infos.type";
import { ITeam } from "@teammood/team";
import { UserService } from "@teammood/shared/util/user";
import { Author, RichTextComment, Thread, ThreadAuthor, TimelineItemDialogService } from "@teammood/timeline";
import { CompositeFilter, FilterCodec } from "@teammood/filter";
import { catchError, EMPTY, filter, mergeMap, of, Subject, takeUntil, tap } from "rxjs";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { LoggerService } from "@teammood/shared/util/logger";
import { ClosedOptions, PollTemplate, PollUnderCreation } from "../../polls/create-poll/create-poll.type";
import { TranslateService } from "@ngx-translate/core";
import { MoodsService } from "../../timeline/timeline-item/moods/moods.service";
import { ActivatedRoute, Router } from "@angular/router";
import { ManagerCommentUpdate } from "../manager-comment-review/manager-comment-review.component";

@Component({
    selector: 'task-details',
    templateUrl: './task-details.component.html',
    styleUrls: ['./task-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskDetailsComponent implements OnInit, OnDestroy {

    protected task: Task;
    protected userInfos: UserInfos;
    protected teams: ITeam[];
    protected userId: string;

    protected newComment: RichTextComment;
    protected commentAuthors: ThreadAuthor[];
    protected managerListForMentions: ThreadAuthor[];
    protected data: {task: Task, action: TaskAction}
    protected threadInCreation: boolean = false;
    protected author: Author;
    protected archiveTaskAction: TaskAction;
    protected filteredReviews: Review[];
    protected labelSelection: TaskLabel[];

    protected taskInfos: { task: Task, managers: Manager[]};
    protected managersTooltipContent: string;

    @Input() selectedFilter: CompositeFilter;

    private _destroy$ = new Subject<void>();

    constructor(
        private taskbookService: TaskbookService,
        private timelineItemDialogService: TimelineItemDialogService,
        private moodsService: MoodsService,
        private userService: UserService,
        private translate: TranslateService,
        private snackBarService: SnackBarService,
        private filterCodec: FilterCodec,
        private logger: LoggerService,
        private route: ActivatedRoute,
        private router: Router,
    ){}

    async ngOnInit() {

        this.route.params.pipe(
            filter(params => !!params['itemType'] && !!params['itemId']),
            mergeMap(params => {
                const itemType = params['itemType'];
                const itemId = params['itemId'];
                return this.taskbookService.taskDetails(itemType, itemId);
            }),
            tap(async (taskInfos: { task: Task, managers: any[]}) => {
                this.taskInfos = taskInfos;
                this.task = this.taskInfos.task;
                this.labelSelection = this.task.labels;
                this.newComment = RichTextComment.createNew();
                this.archiveTaskAction = this.taskbookService.getArchiveDetailActions(this.taskInfos.task);
                this.commentAuthors = this.taskInfos.managers.map(manager => ({id: manager.id, name: manager.fullname, type: 'NonAnonymousAuthor', firstname: manager.firstname, lastname: manager.lastname}));
                this.managersTooltipContent = this.getManagersTooltipContent();
            }),
            mergeMap(() => {
                return this.userService.get().then(async (user) => {
                    if (user) {
                        this.userId = user.id;
                        this.filteredReviews = this.taskbookService.getFilteredReviews(this.task.reviews, this.userId);
                        this.userInfos = new UserInfos(user.firstname, user.lastname);
                        this.teams = [...user.teams];
                        this.author = {id: user.id, fullname: user.fullname};
                        this.managerListForMentions = this.commentAuthors.filter(commentAuthor => commentAuthor.id !== this.userId);
                    }
                });
            }),
            takeUntil(this._destroy$)
        ).subscribe();

        this.route.queryParams.pipe(
            filter(params => !!params['filter']),
            tap((params) => {
                const filter = params['filter'];
                this.selectedFilter = this.filterCodec.decode(filter);
            }),
            takeUntil(this._destroy$)
        ).subscribe();

    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }


    onAction(data: {task: Task, action: TaskAction}){
        if(data){
            let action: TaskActionDTO;
            switch(data.action.id){
                case 'archive': 
                    action = new ArchiveTaskActionDTO(data.task.id, data.task.itemType, this.selectedFilter);
                    this.saveAction(action);
                    break;
                case 'thread': 
                    this.threadInCreation = true; 
                    break;
                case 'kudos': 
                    this.kudos(data.task);
                    break;
                case 'poll': 
                    this.poll(data.task);
                    break;
            }
        }
    }

    onThreadCreation(data: {threadContent: Thread}){
        if(data?.threadContent){
            const action = new AddThreadTaskActionDTO(this.task.id, this.task.itemType, this.selectedFilter, data.threadContent.message, data.threadContent.isAnonymous);
            this.saveAction(action);
        }
        this.threadInCreation = false; 
    }

    protected saveAction(action: TaskActionDTO){
        this.taskbookService.taskDetailAction(action).pipe(
            mergeMap((task: Task) => {
                this.task = task;
                this.filteredReviews = this.taskbookService.getFilteredReviews(this.task.reviews, this.userId);
                switch(action.taskActionContent.id){
                    case 'archive': 
                        return of(this.router.navigate(['/backlog']));
                    default: return EMPTY;
                }
            }),
            catchError(error => {
                this.logger.error('Error saving action:', error?.text);
                this.snackBarService.error('Oops')
                return EMPTY;
            })
        ).subscribe();
    }

    protected async poll(task: Task){
        const pollTemplates: PollTemplate[] = [
            {
                id: task.id,
                label: this.translate.instant('task_action.poll.template'),
                answerOptions: new ClosedOptions([
                    {
                        label: this.translate.instant('Yes')
                    },
                    {
                        label: this.translate.instant('No')
                    }
                ])
            }
        ];
        const pollDialogRef = await this.timelineItemDialogService.createPoll(this.teams, pollTemplates, '');
        pollDialogRef.beforeClosed().pipe(
            filter((poll) => !!poll),
            tap(async (poll: PollUnderCreation) => {
                const action = new CreatePollTaskActionDTO(task.id, task.itemType, this.selectedFilter, poll);
                this.saveAction(action);
                if(action.taskActionContent._serializePoll){
                    this.poll(task);
                    this.snackBarService.success(this.translate.instant('question_created'));
                }
            }),
            takeUntil(pollDialogRef.afterClosed())
        ).subscribe();
    }

    protected async kudos(task: Task) {
        const dialogRef = await this.timelineItemDialogService.createKudos(this.teams, this.author);
        dialogRef.beforeClosed().pipe(
            filter((kudos) => !!kudos),
            tap(kudos => {
                if (kudos.message?.body) {
                    kudos.message.body = this.moodsService.addSpaceBeforeLink(kudos.message.body);
                    const action = new CreateKudosTaskActionDTO(task.id, task.itemType, this.selectedFilter, kudos, kudos.teamId);
                    this.saveAction(action);
                }
            }),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    onCommentAdded(comment: RichTextComment){
        if(comment){
            const action = new ManagerCommentTaskActionDTO(this.task.id, this.task.itemType, this.selectedFilter, comment, false);
            this.saveAction(action);
        }
        this.newComment = comment;
    }

    getManagersTooltipContent(): string{
        const content = this.taskInfos.managers.map((manager, index) => {
            if(index >=3){
                return manager.fullname;
            }
        }).filter(manager => !!manager).join(', ')
        return content;
    }

    editReviewComment(managerCommentReviewUpdate: ManagerCommentUpdate){
        this.taskbookService.updateManagerCommentReview(this.task.itemType, this.task.id, managerCommentReviewUpdate.review, managerCommentReviewUpdate.updatedComment).pipe(
            tap((updatedTask) => {
                this.task = updatedTask;
                this.filteredReviews = this.taskbookService.getFilteredReviews(this.task.reviews, this.userId);
                this.snackBarService.success('Comment updated');
            }),
            catchError(error => {
                this.logger.error('Error updating comment:', error?.text);
                this.snackBarService.error('Oops')
                return EMPTY;
            })
        ).subscribe();
    }

    deleteReviewComment(managerCommentReview: Review){
        this.taskbookService.deleteReview(this.task.itemType, this.task.id, managerCommentReview).pipe(
            tap(() => {
                this.snackBarService.success('Comment deleted');
                this.filteredReviews = this.filteredReviews.filter(review => review.id !== managerCommentReview.id);
            }),
            catchError(error => {
                this.logger.error('Error deleting comment:', error?.text);
                this.snackBarService.error('Oops')
                return EMPTY;
            })
        ).subscribe();
    }


    onApplyLabelSelection(labels: TaskLabel[]) {
        this.labelSelection = labels;
        const action = new UpdateLabelTaskActionDTO(this.task.id, this.task, labels, this.selectedFilter);
        this.saveAction(action)
    }


    protected removeAssignee(assignee: TaskAssignee) {
        const assignees = [assignee]
        const action = new RemoveAssigneeTaskActionDTO(this.task.id, this.task, assignees, this.selectedFilter);
        this.saveAction(action);
    }

    onAssigneeSelectionChanged(assignees: TaskAssignee[]) {
        this.task.assignees = assignees;
        const action = new AddAssigneeTaskActionDTO(this.task.id, this.task, assignees, this.selectedFilter);
        this.saveAction(action);
    }

}

export interface Manager{
    id: string;
    fullname: string;
    firstname: string;
    lastname: string;
}